import { useCallback, useSyncExternalStore } from "react";
import { useGetWorkbenchContentQuery, useLazyGetWorkbenchContentQuery, type WorkbenchContentQuery } from "./workbenchApi";
import type { WorkbenchGroup } from "./contentSchemas";

interface SharedTailState {
  base: ReturnType<typeof useGetWorkbenchContentQuery>["currentData"];
  groups: Record<string, WorkbenchGroup>;
  loading: string;
  error: string;
}

interface SharedTailStore {
  generation: number;
  listeners: Set<() => void>;
  state: SharedTailState;
}

const emptyTail = (): SharedTailState => ({ base: undefined, groups: {}, loading: "", error: "" });
const noTail = emptyTail();

// snapshot 只读 Map；真正注册延后到 useSyncExternalStore 的 subscribe 生命周期，
// React 放弃尚未 commit 的 render 时不会留下无法退订的 scope。
export function createWorkbenchTailRegistry() {
  const stores = new Map<string, SharedTailStore>();
  return {
    snapshot: (key: string) => stores.get(key)?.state ?? noTail,
    current: (key: string) => stores.get(key),
    size: () => stores.size,
    subscribe: (key: string, listener: () => void) => {
      let store = stores.get(key);
      if (!store) {
        store = { generation: 0, listeners: new Set(), state: emptyTail() };
        stores.set(key, store);
      }
      const subscribed = store;
      subscribed.listeners.add(listener);
      return () => {
        subscribed.listeners.delete(listener);
        queueMicrotask(() => {
          if (subscribed.listeners.size || stores.get(key) !== subscribed) return;
          subscribed.generation++;
          stores.delete(key);
        });
      };
    },
  };
}

const sharedTails = createWorkbenchTailRegistry();

export function workbenchContentScopeKey(input: WorkbenchContentQuery) {
  return JSON.stringify([
    input.userId,
    input.organizationId,
    input.shopBindingId ?? "",
    input.adAccountBindingId ?? "",
    input.market ?? "",
    input.category ?? "",
    input.cardType ?? "",
    [...(input.cardTypes ?? [])].sort(),
  ]);
}

function publish(store: SharedTailStore, state: SharedTailState) {
  store.state = state;
  store.listeners.forEach(listener => listener());
}

function reset(store: SharedTailStore) {
  store.generation++;
  publish(store, emptyTail());
}

export function mergeWorkbenchGroup(current: WorkbenchGroup, next: WorkbenchGroup): WorkbenchGroup {
  const sameScope = current.effective_scope.organization_id === next.effective_scope.organization_id
    && current.effective_scope.shop_binding_id === next.effective_scope.shop_binding_id
    && current.effective_scope.ad_account_binding_id === next.effective_scope.ad_account_binding_id
    && current.effective_scope.market === next.effective_scope.market
    && current.effective_scope.category === next.effective_scope.category
    && current.effective_scope.binding_state === next.effective_scope.binding_state;
  if (current.card_type !== next.card_type || !sameScope) throw new Error("workbench_page_scope_mismatch");
  if (current.state !== "ready" || next.state !== "ready" || current.items.some(item => item.state !== "ready") || next.items.some(item => item.state !== "ready")) throw new Error("workbench_page_state_mismatch");
  const contentVersion = current.items[0]?.content_version;
  if (!contentVersion || current.items.some(item => item.content_version !== contentVersion) || next.items.some(item => item.content_version !== contentVersion)) throw new Error("workbench_page_version_mismatch");
  if (!current.has_more || !current.next_cursor || !next.items.length || current.total !== next.total || current.items.length >= current.total) throw new Error("workbench_page_pagination_mismatch");
  if (next.has_more === (next.next_cursor === "") || next.next_cursor === current.next_cursor) throw new Error("workbench_page_cursor_mismatch");
  const currentIDs = new Set(current.items.map(item => item.content_id));
  const nextIDs = next.items.map(item => item.content_id);
  if (currentIDs.size !== current.items.length || new Set(nextIDs).size !== nextIDs.length || nextIDs.some(id => currentIDs.has(id))) throw new Error("workbench_page_duplicate_content");
  const items = [...current.items, ...next.items];
  if (items.length > current.total || (next.has_more ? items.length >= current.total : items.length !== current.total)) throw new Error("workbench_page_total_mismatch");
  return {
    card_type: current.card_type,
    state: "ready",
    effective_scope: current.effective_scope,
    items,
    total: current.total,
    next_cursor: next.next_cursor,
    has_more: next.has_more,
  };
}

export function useWorkbenchContent(input: WorkbenchContentQuery, skip: boolean, pollingInterval = 30_000) {
  const query = useGetWorkbenchContentQuery(input, {
    skip,
    pollingInterval,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [load] = useLazyGetWorkbenchContentQuery();
  const key = workbenchContentScopeKey(input);
  const subscribe = useCallback((listener: () => void) => sharedTails.subscribe(key, listener), [key]);
  const snapshot = useCallback(() => sharedTails.snapshot(key), [key]);
  const shared = useSyncExternalStore(subscribe, snapshot, snapshot);
  const current = shared.base === query.currentData ? shared : undefined;
  const groups = query.currentData?.groups.map(group => current?.groups[group.card_type] ?? group) ?? [];
  const more = async (type: string) => {
    const store = sharedTails.current(key);
    if (!store) return;
    const group = groups.find(candidate => candidate.card_type === type);
    const base = query.currentData;
    const active = store.state.base === base ? store.state : emptyTail();
    if (!group?.has_more || active.loading) return;
    const generation = store.generation;
    publish(store, { base, groups: active.groups, loading: type, error: "" });
    try {
      const scope = { userId: input.userId, organizationId: input.organizationId, shopBindingId: input.shopBindingId, adAccountBindingId: input.adAccountBindingId, market: input.market, category: input.category };
      const result = await load({ ...scope, cardType: group.card_type, cursor: group.next_cursor }).unwrap();
      if (sharedTails.current(key) !== store || generation !== store.generation || store.state.base !== base) return;
      const next = result.groups.find(candidate => candidate.card_type === type);
      if (!next) throw new Error("缺少卡片分组");
      publish(store, {
        ...store.state,
        loading: "",
        groups: { ...store.state.groups, [type]: mergeWorkbenchGroup(group, next) },
      });
    } catch {
      if (sharedTails.current(key) === store && generation === store.generation && store.state.base === base) publish(store, { ...store.state, loading: "", error: type });
    }
  };
  const retry = () => {
    // 首批相同响应可能被 RTK Query 结构共享；先推进代次并清除共享尾页，旧回执不能重新落屏。
    const store = sharedTails.current(key);
    if (store) reset(store);
    if (!query.isUninitialized) void query.refetch();
  };
  return { query, groups, more, retry, loading: current?.loading ?? "", error: current?.error ?? "" };
}
